import {catchError, map, switchMap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpService} from '../http-service.service';
import {ApiConst} from '../api-const';
import {EMPTY, Observable, of, Subject} from 'rxjs';
import {CustomerUser} from '../../../model/customer-user';
import {CustomerGroup} from '../../../model/customer-group';
import {ExpressConfig} from "../../../model/express-config";
import {ErlConfig} from "../../../model/erl-config";
import {EmailConfig} from "../../../model/email-config";
import {HttpHeaders} from "@angular/common/http";
import {JwtHelperService} from "@auth0/angular-jwt";
import {environment} from "../../../../environments/environment";
import {CommonService} from "./common.service";


@Injectable()
export class UserManagementService {
  public selectedGroup: any;
  public groupSelectedEvent: Subject<any>;
  public groupAddressEvent: Subject<any>;
  jwtHelper: JwtHelperService;

  constructor(private httpService: HttpService, private commonService: CommonService) {
    this.groupSelectedEvent = new Subject();
    this.groupAddressEvent = new Subject();
  }

  public getCustomerGroups(): Observable<any> {
    return this.httpService.$post(ApiConst.getGroupList, '').pipe(map((res: any) => {
      return res.body;
    }));
  }

  public listCustomerGroups(): Observable<any[]> {
    return this.httpService.$post(ApiConst.listCustomerGroups, '').pipe(map((res: any) => {
      return res.body.listObjOrgGrpUser;
    }));
  }

  public selectGroup(group: any): void {
    this.selectedGroup = group;
    this.groupSelectedEvent.next(this.selectedGroup);
  }

  public getGroupUsers(groupId): Observable<any[]> {
    return this.httpService.$post(ApiConst.getGroupUsers, {groupId: groupId}).pipe(map(res => {
      this.groupAddressEvent.next(res.body.datas.address);
      return res.body.datas.users;
    }));
  }

  public getCustomerUsers(): Observable<any> {
    return this.httpService.$post(ApiConst.getCustomerUsers, '').pipe(map(res => {
      return res.body.datas;
    }));
  }

  public getCustomerGroupUsers(groupId): Observable<any[]> {
    return this.httpService.$post(ApiConst.listCustomerGroupUsers, {groupId: groupId}).pipe(map(res => {
      return res.body.datas.users;
    }));
  }

  public lockCustomerForGroupEdition(): Observable<any[]> {
    return this.httpService.$post(ApiConst.lockCustomerForGroupEdition, '').pipe(map(res => {
      return res.body;
    }));
  }

  public createGroup(group: CustomerGroup): Observable<any[]> {
    return this.httpService.$post(ApiConst.createGroup, group).pipe(map(res => {
      return res.body;
    }));
  }

  public updateGroup(group: CustomerGroup): Observable<any[]> {
    return this.httpService.$post(ApiConst.updateGroup, group).pipe(map(res => {
      return res.body;
    }));
  }

  public saveGroupChain(groupChain: any): Observable<any> {
    return this.httpService.$post(ApiConst.saveGroupChains, groupChain).pipe(map(res => {
      return res.body;
    }));
  }

  public getGroupChain(groupId: string): Observable<any> {
    const dataToSend = new FormData();
    dataToSend.append('groupId', groupId);
    return this.httpService.$post(ApiConst.getGroupChains, dataToSend).pipe(map(res => {
      return res.body.datas;
    }));
  }

  public createUser(user: CustomerUser, groupId: string): Observable<any> {
    const params = {
      groupId: groupId,
      userId: user.sUserID,
      firstName: user.sUserFirstName,
      lastName: user.sUserLastName,
      email: user.sUserEmail,
      lang: user.sLangID,
      title: user.title,
      businessCategory: user.businessCategory,
      password: user.sUserPwd
    };
    return this.httpService.$post(ApiConst.createUser, params).pipe(map((res: any) => {
      return res.body;
    }));
  }

  public importUsers(users: any[]): Observable<any> {
    return this.httpService.$post(ApiConst.importUsers, {users: users}).pipe(map(response => {
      return response.body;
    }));
  }

  public updateUser(user: CustomerUser, groupId: string): Observable<any> {
    const params = {
      groupId: groupId,
      userId: user.sUserID,
      firstName: user.sUserFirstName,
      lastName: user.sUserLastName,
      email: user.sUserEmail,
      lang: user.sLangID,
      title: user.title,
      businessCategory: user.businessCategory
    };
    /*
    if (user.sUserPwd) {
      params['password'] = user.sUserPwd;
    }*/
    return this.httpService.$post(ApiConst.updateUser, params).pipe(map(res => {
      return res.body;
    }));
  }

  public getListAnalyticID(): Observable<string[]> {
    return this.httpService.$post(ApiConst.analyticIDList, "").pipe(map(res => {
      return res.body.datas;
    }));
  }

  public getErlConfig(): Observable<ErlConfig> {
    return this.httpService.$post(ApiConst.getErlConfig, "").pipe(map(res => {
      return res.body.datas;
    }));
  }

  public saveErlConfig(config: ErlConfig): Observable<ErlConfig> {
    return this.httpService.$post(ApiConst.saveErlConfig, config).pipe(map(res => {
      return res.body.datas;
    }));
  }

  public getEmailConfig(): Observable<EmailConfig> {
    return this.httpService.$post(ApiConst.getEmailConfig, "").pipe(map(res => {
      return res.body.datas;
    }));
  }

  public saveEmailConfig(config: EmailConfig): Observable<EmailConfig> {
    return this.httpService.$post(ApiConst.saveEmailConfig, config).pipe(map(res => {
      return res.body.datas;
    }));
  }

  public getExpressConfig(): Observable<ExpressConfig> {
    return this.httpService.$post(ApiConst.getExpressConfig, "").pipe(map(res => {
      return res.body.datas;
    }));
  }

  public saveExpressConfig(config: ExpressConfig): Observable<ExpressConfig> {
    return this.httpService.$post(ApiConst.saveExpressConfig, config).pipe(map(res => {
      return res.body.datas;
    }));
  }

  public getPurgeConfig(): Observable<bigint[]> {
    return this.httpService.$post(ApiConst.getPurgeConfig, "").pipe(map(res => {
      if (res.body.datas && res.body.datas.length > 1 && typeof res.body.datas[0] == "bigint" && typeof res.body.datas[1] == "bigint") {
        return res.body.datas;
      } else {
        return [80, 3650]
      }
    }));
  }

  public savePurgeConfig(purgeConfigs: bigint[]): Observable<bigint[]> {
    return this.httpService.$post(ApiConst.savePurgeConfig, purgeConfigs).pipe(map(res => {
      return res.body.datas;
    }));
  }

  public exportWso2(customerId: string): Observable<any> {
    const params = {customerId: customerId};

    return this.httpService.$post(ApiConst.exportWSO2, params).pipe(map(response => {
      return response.body.datas;
    }));
  }

  //WSO HEADERS
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('wsoAccessToken');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*'
    });
  }

  //<editor-fold desc="Users Management">
  public getWsoUserIdByUsername(username: string): Observable<string|null> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Users?filter=userName eq ${username}&attributes=id`;
    const headers = this.getAuthHeaders();

    return this.httpService.get<any>(url, {headers}).pipe(
      map(response => {
        if ('Resources' in response && 'id' in response.Resources[0]) {
          return response.Resources[0].id;
        }
        return null
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public getWsoUserById(userId: string): Observable<any> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Users/${userId}`;
    const headers = this.getAuthHeaders();

    return this.httpService.get<any>(url, {headers}).pipe(
      map(response => {
        if (response && 'id' in response) {
          return response;
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public createWsoUser(user: CustomerUser, groupId: string): Observable<string> {
    const params = {
      groupId: groupId,
      userId: user.sUserID,
      firstName: user.sUserFirstName,
      lastName: user.sUserLastName,
      email: user.sUserEmail,
      lang: user.sLangID,
      title: user.title,
      businessCategory: user.businessCategory
    };

    let language: string = 'Francais';

    if (user.sLangID == 'en_US') {
      language = 'English'
    }

    const headers = this.getAuthHeaders();

    const body = {
      "schemas": [
        "urn:ietf:params:scim:schemas:core:2.0:User",
        "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User",
        "urn:scim:wso2:schema"
      ],
      "userName": `${params.userId}`,
      "name": {
        "givenName": `${params.firstName}`,
        "familyName": `${params.lastName}`
      },
      "emails": [
        {
          "value": `${params.email}`
        }
      ],
      "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User": {
        "country": `${language}`,
        "askPassword": true
      },
      "urn:scim:wso2:schema": {
        "CodeClient": `${localStorage.getItem(environment.name + environment.customerIdInStorage).replaceAll('\"', '')}`,
        "LDAP_user": `${params.userId}`,
        "groupeClient": `${groupId}`
      }
    }

    return this.httpService.post<any>(`${environment.wso2.wso2BaseUrl}/scim2/Users`, JSON.stringify(body), {headers}).pipe(
      map((response: any) => {
        if ('id' in response) {
          return response.id;
        }
        throw new Error('Erreur interne');
      }),
      catchError((error: any) => {
        if (error.status === 409) {
          this.commonService.addToast({
            life: 3000,
            severity: 'error',
            summary: "Opération impossible",
            detail: "Ce nom d'utilisateur n'est pas disponible"
          });
        } else {
          this.commonService.addToast({
            life: 3000,
            severity: 'error',
            summary: 'Echec de l\'opération',
            detail: error.message
          });
        }
        return EMPTY;
      })
    );
  }

  public updateWsoUser(wsoUser: any): Observable<boolean> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Users/${wsoUser.id}`;
    const headers = this.getAuthHeaders();

    let country = 'France';
    if (wsoUser['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].country != 'Francais') {
      country = 'United Kingdom';
    }
    const body = {
      "schemas": [
        "urn:ietf:params:scim:schemas:core:2.0:User",
        "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User",
        "urn:scim:wso2:schema"
      ],
      "userName": `${wsoUser.userName}`,
      "name": {
        "givenName": `${wsoUser.name.givenName}`,
        "familyName": `${wsoUser.name.familyName}`
      },
      "emails": [
        {
          "value": `${wsoUser.emails[0]}`
        }
      ],
      "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User": {
        "country": `${country}`
      },
      "urn:scim:wso2:schema": {
        "CodeClient": `${wsoUser['urn:scim:wso2:schema'].CodeClient}`,
        "LDAP_user": `${wsoUser['urn:scim:wso2:schema'].LDAP_user}`,
        "groupeClient": `${wsoUser['urn:scim:wso2:schema'].groupeClient}`
      }
    }

    return this.httpService.put<any>(url, body, {headers}).pipe(
      map(response => {
        if ('id' in response && response.id === wsoUser.id) {
          return true;
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public updateWsoUserWithLdapUser(wsoUser: any, ldapUser: CustomerUser): Observable<boolean> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Users/${wsoUser.id}`;
    const headers = this.getAuthHeaders();

    let country = 'France';
    if (ldapUser.sLangID != 'fr_FR') {
      country = 'United Kingdom';
    }
    const body = {
      "schemas": [
        "urn:ietf:params:scim:schemas:core:2.0:User",
        "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User",
        "urn:scim:wso2:schema"
      ],
      "userName": `${wsoUser.userName}`,
      "name": {
        "givenName": `${ldapUser.sUserFirstName}`,
        "familyName": `${ldapUser.sUserLastName}`
      },
      "emails": [
        {
          "value": `${ldapUser.sUserEmail}`
        }
      ],
      "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User": {
        "country": `${country}`
      },
      "urn:scim:wso2:schema": {
        "CodeClient": `${wsoUser['urn:scim:wso2:schema'].CodeClient}`,
        "LDAP_user": `${wsoUser['urn:scim:wso2:schema'].LDAP_user}`,
        "groupeClient": `${wsoUser['urn:scim:wso2:schema'].groupeClient}`
      }
    }

    return this.httpService.put<any>(url, body, {headers}).pipe(
      map(response => {
        if ('id' in response && response.id === wsoUser.id) {
          return true;
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public askWsoResetPassword(userId: string): Observable<boolean> {
    const url = `${environment.wso2.wso2BaseUrl}/t/carbon.super/scim2/Users/${userId}`;
    const headers = this.getAuthHeaders();

    const body = {
      "Operations": [
        {
          "op": "add",
          "value": {
            "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User": {
              "forcePasswordReset": true
            }
          }
        }
      ],
      "schemas": [
        "urn:ietf:params:scim:api:messages:2.0:PatchOp"
      ]
    };

    return this.httpService.patch<any>(url, body, {headers}).pipe(
      map(response => {
        return response && response.status === 'SUCCESS';
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  //</editor-fold>
  //<editor-fold desc="Groups Management">
  public getWsoGroupIdByName(groupName: string): Observable<string | null> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Groups?filter=displayName eq ${groupName}&attributes=id`;
    const headers = this.getAuthHeaders();

    return this.httpService.get<any>(url, {headers}).pipe(
      map(response => {
        if (response.Resources && response.Resources.length > 0) {
          if (response.Resources && response.Resources.length > 0) {
            if ('Resources' in response && 'id' in response.Resources[0]) {
              return response.Resources[0].id;
            }
          }
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public addWsoGroupMemberByUsername(userId: string, userName: string, groupId: string): Observable<boolean> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Groups/${groupId}`;
    const headers = this.getAuthHeaders();

    const body = {
      schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"],
      Operations: [
        {
          op: "add",
          value: {
            members: [
              {
                display: userName,
                value: userId
              }
            ]
          }
        }
      ]
    };

    return this.httpService.patch<any>(url, body, {headers}).pipe(
      map(response => {
        return response && response.id === groupId;
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  //</editor-fold>
  //<editor-fold desc="Roles Management">
  public getWsoRoleIdByName(roleName: string): Observable<string | null> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/Roles?filter=displayName eq "${roleName}"&attributes=id`;
    const headers = this.getAuthHeaders();

    return this.httpService.get<any>(url, {headers}).pipe(
      map(response => {
        if ('Resources' in response && 'id' in response.Resources[0]) {
          return response.Resources[0].id;
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public addWsoUserToRole(userId: string, roleId: string): Observable<string | null> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/v2/Roles/${roleId}`;
    const headers = this.getAuthHeaders();

    const body = {
      "schemas": [
        "urn:ietf:params:scim:api:messages:2.0:PatchOp"
      ],
      "Operations": [
        {
          "op": "add",
          "path": "users",
          "value": [
            {
              "value": `${userId}`
            }
          ]
        }
      ]
    };

    return this.httpService.patch<any>(url, body, {headers}).pipe(
      map(response => {
        if ('id' in response) {
          return response.id;
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

  public removeWsoUserFromRole(userId: string, roleId: string): Observable<any> {
    const url = `${environment.wso2.wso2BaseUrl}/scim2/v2/Roles/${roleId}`;
    const headers = this.getAuthHeaders();

    const body = {
      "schemas": [
        "urn:ietf:params:scim:api:messages:2.0:PatchOp"
      ],
      "Operations": [
        {
          "op": "remove",
          "path": `users[value eq ${userId}]`
        }
      ]
    };

    return this.httpService.patch<any>(url, body, {headers}).pipe(
      map(response => {
        if ('id' in response) {
          return response.id;
        }
        throw new Error('Erreur interne');
      }), catchError((error: any) => {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: 'Echec de l\'opération',
          detail: error.message
        });
        return EMPTY;
      })
    );
  }

//</editor-fold>
}
